import { useEffect, useState } from 'react';
import { signIn } from 'next-auth/react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Stack } from '@mui/material';
import styled from '@emotion/styled';

import { useToggle } from '@dh/hooks';
import { PMPSnackbar, WTSHLogo } from '@dh/components';
import { LoginError } from '@dh/utils/loginUtils';

import { ROUTES } from '../../../constants';
import { LoginCard } from './login-card/LoginCard';
import { shouldForwardProps } from '@dh/utils';
import { SHStateChancelleryLogo } from '@dh/components/svgs/SHStateChancelleryLogo';
import { EchtDigitalLogo } from '@dh/components/svgs/EchtDigitalLogo';
import { WrapContainer } from '@dh/styles';

const LoginScreenBackground = styled(Stack, shouldForwardProps)`
    background: linear-gradient(172.5deg, #052752 0%, #0a396d 77.5%, transparent 77.5%);
    height: 100%;
    align-items: flex-end;
`;

export const LoginScreen = () => {
    const [loginError, setLoginError] = useState<LoginError | undefined>();
    const router = useRouter();
    const searchParams = useSearchParams();
    const t = useTranslations('login');
    const [showSnackbar, toggleShowSnackbar] = useToggle();
    const [snackbarText, setSnackbarText] = useState('');

    useEffect(() => {
        const confirmedParam = searchParams.get('confirmed');

        if (confirmedParam === 'true') {
            setSnackbarText(t('login_confirmed_hint'));
            toggleShowSnackbar(true);
        }
    }, [searchParams, toggleShowSnackbar, t]);

    const onLogin = async (email: string, password: string) => {
        // reset an existing error before executing the login so that we have a visual feedback for new errors
        setLoginError(undefined);
        const res = await signIn('strapiLogin', {
            redirect: false,
            email,
            password
        });

        if (!res || res.error) {
            if (res?.code === LoginError.Credentials) {
                setLoginError(LoginError.Credentials);
            } else if (res?.code === LoginError.Unconfirmed) {
                setLoginError(LoginError.Unconfirmed);
            } else if (res?.code === LoginError.NotApproved) {
                setLoginError(LoginError.NotApproved);
            } else {
                setLoginError(LoginError.Unknown);
            }
        } else {
            const callbackUrl = searchParams.get('from');
            const redirectUrl = callbackUrl ?? ROUTES.Home;
            router.replace(redirectUrl);
        }
    };

    return (
        <LoginScreenBackground>
            <Stack width={'100%'} alignItems={'center'}>
                <LoginCard error={loginError} onLogin={onLogin} />
                <PMPSnackbar
                    isOpen={showSnackbar}
                    toggleIsOpen={toggleShowSnackbar}
                    message={snackbarText}
                />
            </Stack>
            <WrapContainer
                justifyContent={'flex-end'}
                alignItems={'flex-end'}
                gap={4}
                mr={4}
                mt={22}
                mb={2}
                height={'100%'}
            >
                <EchtDigitalLogo />
                <SHStateChancelleryLogo />
                <WTSHLogo />
            </WrapContainer>
        </LoginScreenBackground>
    );
};
